import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import 'Features/Bundle/components/KeySellingPointBars/BundlePoints.scss';

import { currencyCode } from 'Helpers/Geo/data';
import BundleGamePoints from 'Features/Bundle/components/KeySellingPointBars/BundleGamePoints';
import BundleAudioPoints from 'Features/Bundle/components/KeySellingPointBars/BundleAudioPoints';
import BundleSoftwarePoints from 'Features/Bundle/components/KeySellingPointBars/BundleSoftwarePoints';
import BundleComicPoints from 'Features/Bundle/components/KeySellingPointBars/BundleComicPoints';
import BundleFallbackPoints from 'Features/Bundle/components/KeySellingPointBars/BundleFallbackPoints';
import BundleMixedPoints from 'Features/Bundle/components/KeySellingPointBars/BundleMixedPoints';
import BundleProductTypes from 'Helpers/Product/BundleProductTypes';

export default function BundleKeySellingPoints({
  product,
  validDrm,
  totalGameCount,
  totalPageCount,
  downloadTypes,
  mixedBundle,
  deliveredGameCount,
}) {
  const productTypes = BundleProductTypes(product.bundles);

  let maxPrice = product.fullPrice;

  let steamCompatible = 0;
  if (product.bundles) {
    product.bundles.forEach((bundleTier) => {
      if (product.bundles.length > 1) {
        const tierMaxPrice = get(bundleTier, `incrementalFullPrice.${currencyCode}`, product.fullPrice);
        if (tierMaxPrice > maxPrice[currencyCode]) { maxPrice = bundleTier.incrementalFullPrice; }
      }

      if (!isEmpty(bundleTier.games)) {
        bundleTier.games.forEach((game) => {
          const deckSupport = get(game, 'steam.deck_support');
          if (game.display && (deckSupport === 'verified' || deckSupport === 'playable')) {
            steamCompatible += 1;
          }
        });
      }
    });
  }

  let showSteamCompatible = false;
  if (steamCompatible / totalGameCount >= 0.65) showSteamCompatible = true;

  // Point Type Conditionals
  const instantDelivery = product.template_type === 'standard_mystery' || product.template_type === 'tiered_mystery';

  if (product.template_type === 'book') return false;

  if (product.template_type === 'audio') {
    return (
      <BundleAudioPoints
        price={product.currentPrice || product.price}
        downloadTypes={downloadTypes}
        discountPercentOverride={product.discount_percent_override}
        fullPrice={product.fullPrice}
        giveaway={product.giveaway}
        maxPrice={maxPrice}
        validDrm={validDrm}
      />
    );
  }

  if (product.template_type === 'comic') {
    return (
      <BundleComicPoints
        maxPrice={maxPrice}
        totalPageCount={totalPageCount}
        mixedBundle={mixedBundle}
        productTypes={productTypes}
        downloadTypes={downloadTypes}
        validDrm={validDrm}
      />
    );
  }

  if (product.template_type === 'software') {
    return (
      <BundleSoftwarePoints
        price={product.price}
        fullPrice={product.fullPrice}
        discountPercentOverride={product.discount_percent_override}
        maxPrice={maxPrice}
        mixedBundle={mixedBundle}
        totalGameCount={totalGameCount}
        validDrm={validDrm}
        tiered={product.tiered || false}
        bundleSlug={product.slug}
      />
    );
  }

  if (mixedBundle) {
    return (
      <BundleMixedPoints
        maxPrice={maxPrice}
        validDrm={validDrm}
        productTypes={productTypes}
        bundleSlug={product.slug}
        productId={product._id}
      />
    );
  }

  if (isEmpty(validDrm[0]) || validDrm[0] === 'drm_free') {
    return (
      <BundleFallbackPoints
        maxPrice={maxPrice}
        validDrm={validDrm}
        totalGameCount={totalGameCount}
      />
    );
  }

  return (
    <BundleGamePoints
      displayType={product.display_type}
      fullPrice={maxPrice}
      instantDelivery={instantDelivery}
      price={product.currentPrice || product.price}
      showSteamCompatible={showSteamCompatible}
      steamCompatible={steamCompatible}
      totalGameCount={totalGameCount}
      validDrm={validDrm}
      deliveredGameCount={deliveredGameCount}
      tiered={product.tiered || false}
      bundleSlug={product.slug}
    />
  );
}

BundleKeySellingPoints.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    cover: PropTypes.string,
    presale: PropTypes.bool,
    type: PropTypes.string,
    currentDiscount: PropTypes.shape({}),
    price: PropTypes.shape({}),
    fullPrice: PropTypes.shape({}),
    hideDiscount: PropTypes.bool,
    bundles: PropTypes.arrayOf(PropTypes.shape({})),
    endDate: PropTypes.string,
    productId: PropTypes.string,
    soldOut: PropTypes.bool,
    regions_included: PropTypes.array,
    regions_excluded: PropTypes.array,
    giveaway: PropTypes.bool,
    platforms: PropTypes.shape({
      windows: PropTypes.bool,
      mac: PropTypes.bool,
      linux: PropTypes.bool,
    }),
    drm: PropTypes.shape({}),
  }),
  validDrm: PropTypes.arrayOf(PropTypes.string),
  totalGameCount: PropTypes.number,
  totalPageCount: PropTypes.number,
  downloadTypes: PropTypes.arrayOf(PropTypes.string),
  mixedBundle: PropTypes.bool,
  deliveredGameCount: PropTypes.number,
};

BundleKeySellingPoints.defaultProps = {
  product: {
    _id: '',
    name: '',
    cover: 'default.jpg',
    presale: false,
    type: 'game',
    currentDiscount: {},
    price: {},
    fullPrice: {},
    hideDiscount: false,
    bundles: [],
    endDate: '',
    productId: '',
    soldOut: false,
    regions_included: [],
    regions_excluded: [],
    giveaway: false,
    platforms: {
      windows: false,
      mac: false,
      linux: false,
    },
    drm: {},
    no_release_date: false,
  },
  validDrm: [],
  totalGameCount: 0,
  totalPageCount: 0,
  downloadTypes: [],
  mixedBundle: false,
  deliveredGameCount: 0,
};
